import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { ProtocolConsumption } from '../../../../models/Entities/Protocols/ProtocolConsumption';
import { ProtocolPatient } from '../../../../models/Entities/Protocols/ProtocolPatient';
import { ProtocolConsumedServiceStatus } from '../../../../models/Entities/Protocols/ProtocolConsumedServiceStatus';
import { statusStage } from '../PatientStatusStatistics.Functions';
import { VisitExecutionModel } from '../VisitExecution.Functions';

/**
 * Control de facturación del contrato.
 *
 * Un contrato clínico es una cadena, y la plata se pierde en los eslabones:
 * se pacta un concepto → el paciente hace la visita → alguien carga el consumo →
 * el consumo se valoriza contra un concepto del contrato → entra en una liquidación
 * → se cobra. Todo lo que hay acá existe para contestar dos preguntas: qué se cobra
 * en qué visita, y dónde se cortó la cadena.
 *
 * Son funciones puras: entran datos del contrato y del protocolo, salen filas.
 */

//#region Estados de liquidación

/**
 * En qué punto del circuito está un consumo.
 *
 * Ojo con los estados del backend: "Pendiente de liquidación" (0) y "Por liquidar"
 * (6) son consumos que TODAVÍA NO entraron en ninguna liquidación, aunque el nombre
 * del segundo suene a hecho. El panel viejo los contaba como liquidados y eso tapaba
 * justamente lo que hay que ver.
 */
export type BillingBucket = 'unsettled' | 'inSettlement' | 'collected' | 'excluded';

export const BUCKET_OF_STATUS: Record<number, BillingBucket> = {
  [ProtocolConsumedServiceStatus.NotSettled]: 'unsettled',
  [ProtocolConsumedServiceStatus.ToSettle]: 'unsettled',
  [ProtocolConsumedServiceStatus.Pending]: 'inSettlement',
  [ProtocolConsumedServiceStatus.Settled]: 'inSettlement',
  [ProtocolConsumedServiceStatus.Invoiced]: 'inSettlement',
  [ProtocolConsumedServiceStatus.PartialSettled]: 'inSettlement',
  [ProtocolConsumedServiceStatus.Payee]: 'collected',
  [ProtocolConsumedServiceStatus.Rejected]: 'excluded',
  [ProtocolConsumedServiceStatus.Ignored]: 'excluded'
};

export const BUCKET_LABEL: Record<BillingBucket, string> = {
  unsettled: 'Sin liquidar',
  inSettlement: 'En liquidación',
  collected: 'Cobrado',
  excluded: 'Fuera de liquidación'
};

export const bucketOf = (consumption: ProtocolConsumption): BillingBucket =>
  BUCKET_OF_STATUS[consumption.status] ?? 'unsettled';

//#endregion Estados de liquidación

//#region Período y helpers

const time = (value?: Date | string | null): number | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? undefined : parsed;
};

/** Consumos cuyo vencimiento cae en el rango. Sin rango, todos. Sin fecha, entra. */
export const filterByPeriod = (
  consumptions: ProtocolConsumption[],
  from?: Date,
  to?: Date
): ProtocolConsumption[] => {
  const fromTime = from ? new Date(from).setHours(0, 0, 0, 0) : undefined;
  const toTime = to ? new Date(to).setHours(23, 59, 59, 999) : undefined;
  if (fromTime === undefined && toTime === undefined) return consumptions ?? [];

  return (consumptions ?? []).filter((consumption) => {
    const due = time(consumption.serviceDueDate);
    if (due === undefined) return true;
    if (fromTime !== undefined && due < fromTime) return false;
    if (toTime !== undefined && due > toTime) return false;
    return true;
  });
};

const amountOf = (consumption: ProtocolConsumption): number => consumption.totalAmount ?? 0;

const sum = (consumptions: ProtocolConsumption[]): number =>
  consumptions.reduce((acc, consumption) => acc + amountOf(consumption), 0);

/** Días desde el vencimiento del servicio; undefined si no tiene fecha. */
export const ageInDays = (consumption: ProtocolConsumption, now: number = Date.now()): number | undefined => {
  const due = time(consumption.serviceDueDate);
  if (due === undefined) return undefined;
  return Math.max(0, Math.floor((now - due) / 86400000));
};

/** Concepto del contrato que valoriza el consumo, mirando también los valores por concepto. */
export const conceptIdOf = (consumption: ProtocolConsumption): number | undefined => {
  if (consumption.contractConceptId) return consumption.contractConceptId;
  const fromValues = (consumption.conceptValues ?? []).find((value) => !!value.contractConceptId);
  return fromValues?.contractConceptId;
};

export const conceptLabel = (concept: ContractConcept): string =>
  concept.concept?.conceptName || concept.concept?.conceptDescription || `Concepto #${concept.id}`;

//#endregion Período y helpers

//#region El circuito del dinero

export interface FunnelStep {
  key: string;
  label: string;
  /** Qué significa, en una línea, para quien no armó el contrato. */
  description: string;
  count: number;
  amount?: number;
  /** Cuántos casos se perdieron respecto del paso anterior. */
  dropCount?: number;
  dropAmount?: number;
}

export interface BillingFunnel {
  currency: string;
  steps: FunnelStep[];
  totals: {
    consumptions: number;
    total: number;
    unsettled: number;
    inSettlement: number;
    collected: number;
    excluded: number;
  };
}

/**
 * El circuito, paso a paso. Cada escalón dice cuántos casos quedaron y cuánta plata,
 * y el salto contra el escalón anterior es exactamente lo que se cayó ahí.
 */
export const buildFunnel = (
  concepts: ContractConcept[],
  stages: ContractStage[],
  consumptions: ProtocolConsumption[],
  visitModel: VisitExecutionModel | undefined
): BillingFunnel => {
  const list = consumptions ?? [];
  const currency = list[0]?.currencyCode ?? '';

  const valued = list.filter((consumption) => !!conceptIdOf(consumption) && amountOf(consumption) > 0);
  const byBucket = (bucket: BillingBucket) => list.filter((consumption) => bucketOf(consumption) === bucket);

  const unsettled = byBucket('unsettled');
  const inSettlement = byBucket('inSettlement');
  const collected = byBucket('collected');
  const excluded = byBucket('excluded');

  const visitsDone = visitModel?.progress.doneTotal ?? 0;
  const visitsExpected = visitModel?.progress.expected ?? 0;

  // El embudo es acumulativo: lo cobrado también pasó por una liquidación. Si cada
  // escalón contara sólo su estado actual, "Cobrado" podría dar más que "En
  // liquidación" y el dibujo mentiría.
  const enteredSettlement = [...inSettlement, ...collected];

  const steps: FunnelStep[] = [
    {
      key: 'agreed',
      label: 'Pactado',
      description: `${concepts.length} conceptos y ${stages.length} visitas configuradas en el contrato`,
      count: concepts.length
    },
    {
      key: 'executed',
      label: 'Ejecutado',
      description: 'Pares paciente-visita con al menos un consumo cargado',
      count: visitsDone,
      dropCount: visitsExpected > visitsDone ? visitsExpected - visitsDone : 0
    },
    {
      key: 'loaded',
      label: 'Cargado',
      description: 'Consumos registrados en el protocolo',
      count: list.length,
      amount: sum(list)
    },
    {
      key: 'valued',
      label: 'Valorizado',
      description: 'Consumos con concepto del contrato y con importe',
      count: valued.length,
      amount: sum(valued),
      dropCount: list.length - valued.length,
      dropAmount: sum(list) - sum(valued)
    },
    {
      key: 'settled',
      label: 'En liquidación',
      description: 'Entraron en una liquidación: liquidados, facturados o ya cobrados',
      count: enteredSettlement.length,
      amount: sum(enteredSettlement),
      dropCount: unsettled.length,
      dropAmount: sum(unsettled)
    },
    {
      key: 'collected',
      label: 'Cobrado',
      description: 'Consumos marcados como pagados',
      count: collected.length,
      amount: sum(collected),
      dropCount: inSettlement.length,
      dropAmount: sum(inSettlement)
    }
  ];

  return {
    currency,
    steps,
    totals: {
      consumptions: list.length,
      total: sum(list),
      unsettled: sum(unsettled),
      inSettlement: sum(inSettlement),
      collected: sum(collected),
      excluded: sum(excluded)
    }
  };
};

//#endregion El circuito del dinero

//#region Qué se cobra dónde

export interface MatrixCell {
  amount: number;
  count: number;
  /** Pacientes distintos a los que se les cobró. */
  patients: number;
}

export interface MatrixRow {
  key: string;
  conceptId?: number;
  label: string;
  /** El concepto está pactado en el contrato. Si no, es un cobro sin respaldo contractual. */
  isContracted: boolean;
  /** Revisado por un usuario en el contrato. */
  isApproved?: boolean;
  cells: Map<string, MatrixCell>;
  total: MatrixCell;
}

export interface MatrixColumn {
  key: string;
  label: string;
  isOptional: boolean;
  total: MatrixCell;
}

export interface BillingMatrix {
  columns: MatrixColumn[];
  rows: MatrixRow[];
  total: MatrixCell;
  currency: string;
}

const emptyCell = (): MatrixCell => ({ amount: 0, count: 0, patients: 0 });

const NO_STAGE = '__sin_etapa__';
const NO_CONCEPT = '__sin_concepto__';

/**
 * La matriz concepto × visita: la traducción literal del anexo económico contra lo
 * que realmente se cobró. Las filas son los conceptos del contrato —estén o no
 * usados—, más una fila para lo que se cobró sin concepto. Las columnas son las
 * visitas del contrato, más una para los consumos sin etapa.
 *
 * Un casillero vacío no siempre es un error: muchos conceptos se cobran en una sola
 * visita. Pero una fila entera vacía, o una columna entera vacía, sí es una pregunta.
 */
export const buildMatrix = (
  concepts: ContractConcept[],
  stages: ContractStage[],
  consumptions: ProtocolConsumption[]
): BillingMatrix => {
  const list = consumptions ?? [];
  const patientsByKey = new Map<string, Set<string>>();

  const columns: MatrixColumn[] = (stages ?? [])
    .slice()
    .sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0) || a.id - b.id)
    .map((stage) => ({
      key: String(stage.id),
      label: stage.stageName || `Visita ${stage.id}`,
      isOptional: !!stage.isOptional,
      total: emptyCell()
    }));

  const rows = new Map<string, MatrixRow>();
  (concepts ?? []).forEach((concept) => {
    rows.set(String(concept.id), {
      key: String(concept.id),
      conceptId: concept.id,
      label: conceptLabel(concept),
      isContracted: true,
      isApproved: concept.contractConceptApproved,
      cells: new Map<string, MatrixCell>(),
      total: emptyCell()
    });
  });

  const add = (row: MatrixRow, columnKey: string, consumption: ProtocolConsumption) => {
    const cell = row.cells.get(columnKey) ?? emptyCell();
    cell.amount += amountOf(consumption);
    cell.count += 1;
    row.cells.set(columnKey, cell);

    const patientKey = `${row.key}::${columnKey}`;
    const patients = patientsByKey.get(patientKey) ?? new Set<string>();
    if (consumption.patientNumberInProtocol) patients.add(consumption.patientNumberInProtocol.trim().toLowerCase());
    patientsByKey.set(patientKey, patients);
  };

  list.forEach((consumption) => {
    const conceptId = conceptIdOf(consumption);
    const rowKey = conceptId ? String(conceptId) : NO_CONCEPT;

    let row = rows.get(rowKey);
    if (!row) {
      row = {
        key: rowKey,
        conceptId,
        label: rowKey === NO_CONCEPT
          ? 'Sin concepto del contrato'
          : consumption.conceptDescription || consumption.serviceName || `Concepto #${conceptId}`,
        // Un consumo puede apuntar a un concepto que ya no está en el contrato vigente.
        isContracted: false,
        cells: new Map<string, MatrixCell>(),
        total: emptyCell()
      };
      rows.set(rowKey, row);
    }

    const columnKey = consumption.stageId ? String(consumption.stageId) : NO_STAGE;
    if (!columns.some((column) => column.key === columnKey)) {
      columns.push({
        key: columnKey,
        label: columnKey === NO_STAGE ? 'Sin visita' : consumption.stageName || `Visita ${consumption.stageId}`,
        isOptional: false,
        total: emptyCell()
      });
    }

    add(row, columnKey, consumption);
  });

  // Totales de fila, de columna y general, ya con los pacientes distintos contados.
  const total = emptyCell();
  const rowList = Array.from(rows.values());

  rowList.forEach((row) => {
    row.cells.forEach((cell, columnKey) => {
      cell.patients = patientsByKey.get(`${row.key}::${columnKey}`)?.size ?? 0;
      row.total.amount += cell.amount;
      row.total.count += cell.count;

      const column = columns.find((item) => item.key === columnKey);
      if (column) {
        column.total.amount += cell.amount;
        column.total.count += cell.count;
      }
      total.amount += cell.amount;
      total.count += cell.count;
    });
  });

  return {
    columns,
    rows: rowList.sort((a, b) => b.total.amount - a.total.amount || a.label.localeCompare(b.label, 'es')),
    total,
    currency: list[0]?.currencyCode ?? ''
  };
};

//#endregion Qué se cobra dónde

//#region Qué puede estar quedando afuera

export type LeakSeverity = 'high' | 'medium' | 'low';

export interface LeakItem {
  key: string;
  /** Qué es: paciente y visita, o nombre del concepto. */
  title: string;
  detail?: string;
  amount?: number;
  /** El importe es una estimación, no un dato: se dice en pantalla. */
  isEstimated?: boolean;
  /** Para poder abrir el caso en otra pantalla. */
  consumptionId?: string;
}

export interface LeakGroup {
  key: string;
  title: string;
  question: string;
  severity: LeakSeverity;
  count: number;
  amount: number;
  isEstimated: boolean;
  /** Qué hacer con esto. */
  action: string;
  items: LeakItem[];
}

/** Valor típico de una visita del contrato: lo que se cobró en ella a otros pacientes. */
const averageByStage = (consumptions: ProtocolConsumption[]): Map<string, number> => {
  const totals = new Map<string, { amount: number; patients: Set<string> }>();

  consumptions.forEach((consumption) => {
    if (!consumption.stageId) return;
    const key = String(consumption.stageId);
    const entry = totals.get(key) ?? { amount: 0, patients: new Set<string>() };
    entry.amount += amountOf(consumption);
    if (consumption.patientNumberInProtocol) entry.patients.add(consumption.patientNumberInProtocol.trim().toLowerCase());
    totals.set(key, entry);
  });

  const averages = new Map<string, number>();
  totals.forEach((entry, key) => {
    const patients = entry.patients.size || 1;
    averages.set(key, Math.round(entry.amount / patients));
  });
  return averages;
};

/**
 * 1. Visitas del plan que un paciente randomizado hizo sin que se cargara ningún
 *    consumo. Es la fuga clásica y la que más plata se lleva.
 *
 * El importe es estimado: se toma lo que esa misma visita facturó en promedio por
 * paciente. Sin una tarifa por visita en el contrato, es la mejor aproximación
 * honesta que se puede dar, y se muestra como estimación.
 */
export const findVisitsWithoutConsumption = (
  visitModel: VisitExecutionModel | undefined,
  consumptions: ProtocolConsumption[]
): LeakGroup => {
  const items: LeakItem[] = [];
  const averages = averageByStage(consumptions ?? []);
  let amount = 0;

  (visitModel?.rows ?? [])
    .filter((row) => row.stage === 'randomized')
    .forEach((row) => {
      (visitModel?.columns ?? [])
        .filter((column) => !column.isOptional)
        .forEach((column) => {
          if (row.cells.has(column.key)) return;
          const estimate = averages.get(column.key) ?? 0;
          amount += estimate;
          items.push({
            key: `${row.numberInProtocol}::${column.key}`,
            title: `Paciente ${row.numberInProtocol} · ${column.name}`,
            detail: 'Visita obligatoria del plan sin ningún consumo cargado',
            amount: estimate,
            isEstimated: true
          });
        });
    });

  return {
    key: 'visitsWithoutConsumption',
    title: 'Visitas hechas sin consumo cargado',
    question: '¿Se hizo la visita y nadie la cobró?',
    severity: 'high',
    count: items.length,
    amount,
    isEstimated: true,
    action: 'Revisar la visita en el registro clínico y cargar el consumo, o correr la sincronización.',
    items
  };
};

/** 2. Consumos cargados que todavía no entraron en ninguna liquidación. */
export const findUnsettledConsumptions = (
  consumptions: ProtocolConsumption[],
  now: number = Date.now()
): LeakGroup => {
  const unsettled = (consumptions ?? []).filter((consumption) => bucketOf(consumption) === 'unsettled');

  const items: LeakItem[] = unsettled
    .slice()
    .sort((a, b) => (ageInDays(b, now) ?? 0) - (ageInDays(a, now) ?? 0))
    .map((consumption) => {
      const age = ageInDays(consumption, now);
      return {
        key: consumption.id,
        consumptionId: consumption.id,
        title: `${consumption.patientNumberInProtocol || 'Sin paciente'} · ${consumption.serviceName || consumption.conceptDescription || 'Consumo'}`,
        detail: [
          consumption.stageName,
          consumption.status === ProtocolConsumedServiceStatus.ToSettle ? 'Marcado por liquidar' : 'Pendiente de liquidación',
          age !== undefined ? `${age} días de antigüedad` : 'Sin fecha de servicio'
        ]
          .filter(Boolean)
          .join(' · '),
        amount: amountOf(consumption)
      };
    });

  return {
    key: 'unsettled',
    title: 'Consumos cargados pero sin liquidar',
    question: '¿Está cargado y nadie lo liquidó?',
    severity: 'high',
    count: items.length,
    amount: sum(unsettled),
    isEstimated: false,
    action: 'Incluirlos en la próxima liquidación, o entender por qué quedaron afuera.',
    items
  };
};

/** 3. Consumos sin concepto del contrato: no hay precio que aplicar. */
export const findConsumptionsWithoutConcept = (consumptions: ProtocolConsumption[]): LeakGroup => {
  const orphans = (consumptions ?? []).filter((consumption) => !conceptIdOf(consumption));

  return {
    key: 'withoutConcept',
    title: 'Consumos sin concepto del contrato',
    question: '¿Con qué precio se cobra esto?',
    severity: 'medium',
    count: orphans.length,
    amount: sum(orphans),
    isEstimated: false,
    action: 'Asociar cada consumo a su concepto del contrato para que se valorice.',
    items: orphans.map((consumption) => ({
      key: consumption.id,
      consumptionId: consumption.id,
      title: `${consumption.patientNumberInProtocol || 'Sin paciente'} · ${consumption.serviceName || 'Consumo sin nombre'}`,
      detail: [consumption.stageName, BUCKET_LABEL[bucketOf(consumption)]].filter(Boolean).join(' · '),
      amount: amountOf(consumption)
    }))
  };
};

/** 4. Conceptos pactados que nunca generaron un consumo en todo el protocolo. */
export const findConceptsNeverBilled = (
  concepts: ContractConcept[],
  consumptions: ProtocolConsumption[]
): LeakGroup => {
  const used = new Set<number>();
  (consumptions ?? []).forEach((consumption) => {
    const conceptId = conceptIdOf(consumption);
    if (conceptId) used.add(conceptId);
    (consumption.conceptValues ?? []).forEach((value) => {
      if (value.contractConceptId) used.add(value.contractConceptId);
    });
  });

  const never = (concepts ?? []).filter((concept) => !used.has(concept.id));

  return {
    key: 'neverBilled',
    title: 'Conceptos pactados que nunca se cobraron',
    question: '¿Se pactó y nunca se usó?',
    severity: 'low',
    count: never.length,
    amount: 0,
    isEstimated: false,
    action: 'Puede ser normal (conceptos por evento). Revisar los que deberían haberse cobrado ya.',
    items: never.map((concept) => ({
      key: String(concept.id),
      title: conceptLabel(concept),
      detail: concept.isOptional ? 'Concepto opcional del contrato' : 'Concepto del contrato sin ningún consumo'
    }))
  };
};

/**
 * Inconsistencia: el consumo figura liquidado o facturado pero no quedó atado a
 * ninguna liquidación. No es una fuga de plata, es un dato que no cierra.
 */
export const findSettledWithoutSettlement = (consumptions: ProtocolConsumption[]): LeakGroup => {
  const orphans = (consumptions ?? []).filter(
    (consumption) => bucketOf(consumption) === 'inSettlement' && !consumption.settlementId && !consumption.settlementIdentifier
  );

  return {
    key: 'settledWithoutSettlement',
    title: 'Liquidados sin liquidación asociada',
    question: '¿Dónde quedó liquidado?',
    severity: 'medium',
    count: orphans.length,
    amount: sum(orphans),
    isEstimated: false,
    action: 'Verificar el estado del consumo: puede ser un cambio manual de estado.',
    items: orphans.map((consumption) => ({
      key: consumption.id,
      consumptionId: consumption.id,
      title: `${consumption.patientNumberInProtocol || 'Sin paciente'} · ${consumption.serviceName || 'Consumo'}`,
      detail: BUCKET_LABEL[bucketOf(consumption)],
      amount: amountOf(consumption)
    }))
  };
};

/** Los detectores, en el orden en que le importan a quien liquida. */
export const buildLeaks = (
  concepts: ContractConcept[],
  consumptions: ProtocolConsumption[],
  visitModel: VisitExecutionModel | undefined,
  now: number = Date.now()
): LeakGroup[] => [
  findVisitsWithoutConsumption(visitModel, consumptions),
  findUnsettledConsumptions(consumptions, now),
  findConsumptionsWithoutConcept(consumptions),
  findSettledWithoutSettlement(consumptions),
  findConceptsNeverBilled(concepts, consumptions)
];

/** Plata en juego: sólo lo que se puede afirmar, más lo estimado aparte. */
export const leakTotals = (groups: LeakGroup[]): { cases: number; certain: number; estimated: number } => ({
  cases: groups.reduce((acc, group) => acc + group.count, 0),
  certain: groups.filter((group) => !group.isEstimated).reduce((acc, group) => acc + group.amount, 0),
  estimated: groups.filter((group) => group.isEstimated).reduce((acc, group) => acc + group.amount, 0)
});

//#endregion Qué puede estar quedando afuera

//#region Pacientes randomizados sin consumos

/** Un paciente randomizado sin un solo consumo es casi siempre un error de carga. */
export const findPatientsWithoutConsumptions = (
  patients: ProtocolPatient[],
  consumptions: ProtocolConsumption[]
): string[] => {
  const withConsumption = new Set(
    (consumptions ?? [])
      .map((consumption) => (consumption.patientNumberInProtocol ?? '').trim().toLowerCase())
      .filter(Boolean)
  );

  return (patients ?? [])
    .filter((patient) => statusStage(patient.status) === 'randomized')
    .filter((patient) => !withConsumption.has((patient.numberInProtocol ?? '').trim().toLowerCase()))
    .map((patient) => patient.numberInProtocol);
};

//#endregion Pacientes randomizados sin consumos
